import { BasicColumnTypes, IOgqlJobResult, IOgqlV1JobResult } from '@opengov/ogql-types';
import {
  createInfixReportColumn,
  createReportColumnMetaData,
  createReportColumnMetaDataFacts,
  createStorageReportColumn,
  EOgqlInfixColumnOperators,
  EReportColumnTypes,
  IConstantReportColumn,
  IInfixReportColumn,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';
import { DateTime } from 'luxon';

import { composeStateMachine } from '../../../../module';
import { DataTableStatus } from '../../../reportExplorer/types';

import { determineOutputTypeFromTypesAndOperand } from '../basicCalculationUtils';
import { getDateFormatFromColumn } from '../getDateFormatFromColumn';
import { luxonDateFormats } from '../types';

const determineOutputTypeFromInputColumns = (infixColumn: IInfixReportColumn) => {
  return determineOutputTypeFromTypesAndOperand(
    infixColumn.leftColumn.type,
    infixColumn.rightColumn.type,
    infixColumn.operator
  );
};

interface IBasicCalculationState {
  basicCalculation: IInfixReportColumn;
  calculationPreviewTableStatus: DataTableStatus;
  queryResult: IOgqlJobResult | IOgqlV1JobResult;
}

export const BasicCalculationInitialState: IBasicCalculationState = {
  basicCalculation: createInfixReportColumn({
    leftColumn: createStorageReportColumn({ type: BasicColumnTypes.Numeric }),
    rightColumn: createStorageReportColumn({ type: BasicColumnTypes.Numeric })
  }),
  calculationPreviewTableStatus: DataTableStatus.Current,
  queryResult: null
};

export const basicCalculationState = composeStateMachine(
  'basic-calculation',
  BasicCalculationInitialState,
  (compose) => ({
    resetBasicCalculation: compose.payloadlessAction((state) =>
      produce(state, (newState) => {
        newState.basicCalculation = createInfixReportColumn();
      })
    ),
    updateValue1: compose.action((state, payload: IReportColumn) =>
      produce(state, (newState) => {
        newState.basicCalculation.leftColumn = payload;
        newState.basicCalculation.rightColumn = createStorageReportColumn();
        newState.basicCalculation.operator = null;
        newState.queryResult = null;

        if (payload.type === BasicColumnTypes.Date && payload.columnType === EReportColumnTypes.storage) {
          newState.basicCalculation.metadata = createReportColumnMetaData({
            facts: createReportColumnMetaDataFacts({
              minimum: DateTime.local().toFormat(luxonDateFormats[getDateFormatFromColumn(payload)]),
              maximum: DateTime.local().toFormat(luxonDateFormats[getDateFormatFromColumn(payload)])
            })
          });
        }
      })
    ),
    updateValue1CustomValue: compose.action((state, payload: string) =>
      produce(state, (newState) => {
        (newState.basicCalculation.leftColumn as IConstantReportColumn<any>).value = payload;
        newState.queryResult = null;
      })
    ),
    updateValue2: compose.action((state, payload: IReportColumn) =>
      produce(state, (newState) => {
        newState.basicCalculation.rightColumn = payload;
        newState.basicCalculation.type = determineOutputTypeFromInputColumns(newState.basicCalculation);
        newState.queryResult = null;
      })
    ),
    updateValue2CustomValue: compose.action((state, payload: string) =>
      produce(state, (newState) => {
        (newState.basicCalculation.rightColumn as IConstantReportColumn<any>).value = payload;
        newState.queryResult = null;
      })
    ),
    updateOperator: compose.action((state, payload: EOgqlInfixColumnOperators) =>
      produce(state, (newState) => {
        newState.basicCalculation.operator = payload;
        newState.basicCalculation.rightColumn = createStorageReportColumn();
        newState.queryResult = null;
      })
    ),
    updateColumnName: compose.action((state, payload: string) =>
      produce(state, (newState) => {
        newState.basicCalculation.displayName = payload;
        newState.queryResult = null;
      })
    ),
    setQueryResult: compose.action((state, payload: any) =>
      produce(state, (newState) => {
        newState.queryResult = payload;
      })
    ),
    updateCalculationPreviewTableStatus: compose.action((state, payload: DataTableStatus) =>
      produce(state, (newState) => {
        newState.calculationPreviewTableStatus = payload;
      })
    )
  })
);

export const {
  actions: basicCalculationActions,
  actionCreators: basicCalculationActionCreators,
  selector: basicCalculationSelector,
  reducer: basicCalculationReducer
} = basicCalculationState;
